/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

const safeStringify = value => {
  try {
    return JSON.stringify(value ?? {});
  } catch (_) {
    return '{}';
  }
};

export const buildAmbientAttachedContext = ({ appRef, routeContext, recentApiCalls = [] } = {}) => {
  const pathname = routeContext?.pathname ?? '';
  const routeHandle = routeContext?.routeHandle ?? {};
  const routeParams = routeContext?.routeParams ?? {};

  const extraStatePayload = {
    routeHandle: safeStringify(routeHandle),
    routeParams: safeStringify(routeParams),
    recentApiCalls: safeStringify(recentApiCalls),
  };

  return {
    viewContext: {
      ...(appRef ? { appRef } : {}),
      ...(pathname ? { route: pathname } : {}),
    },
    extraState: safeStringify(extraStatePayload),
  };
};

export const hasNonEmptyAmbientContext = ambientAttachedContext => {
  if (!ambientAttachedContext || typeof ambientAttachedContext !== 'object') {
    return false;
  }

  const viewContext = ambientAttachedContext.viewContext;
  const hasViewContext = Boolean(viewContext?.appRef) || Boolean(viewContext?.route);

  if (hasViewContext) {
    return true;
  }

  const extraState = ambientAttachedContext.extraState;

  if (typeof extraState !== 'string' || extraState.trim() === '' || extraState === '{}') {
    return false;
  }

  try {
    const parsed = JSON.parse(extraState);

    const routeHandle = parsed?.routeHandle ? JSON.parse(parsed.routeHandle) : {};
    const routeParams = parsed?.routeParams ? JSON.parse(parsed.routeParams) : {};
    const recentApiCalls = parsed?.recentApiCalls ? JSON.parse(parsed.recentApiCalls) : [];

    return Boolean(
      Object.keys(routeHandle).length > 0 ||
        Object.keys(routeParams).length > 0 ||
        (Array.isArray(recentApiCalls) && recentApiCalls.length > 0)
    );
  } catch (_) {
    return extraState.trim().length > 0;
  }
};

export const mergeAttachedContextWithAmbient = (overlayContext = {}, ambientContext = {}) => {
  const merged = {
    ...(overlayContext && typeof overlayContext === 'object' ? overlayContext : {}),
  };

  const overlayViewContext = merged.viewContext && typeof merged.viewContext === 'object' ? merged.viewContext : {};
  const ambientViewContext =
    ambientContext?.viewContext && typeof ambientContext.viewContext === 'object' ? ambientContext.viewContext : {};

  merged.viewContext = {
    ...ambientViewContext,
    ...overlayViewContext,
  };

  if (ambientContext?.extraState) {
    if (merged.extraState && typeof merged.extraState === 'string') {
      merged.extraState = safeStringify({
        ambient: ambientContext.extraState,
        overlay: merged.extraState,
      });
    } else {
      merged.extraState = ambientContext.extraState;
    }
  }

  return merged;
};
